<template>
  <div @click="selectedItem = null">
    <breadcrumbs :home="recurring ? 'Recurring purchase order template' : 'Purchase order'">
      <template v-slot:right>
        <v-menu offset-y v-if="company.permissions.includes('config.write')" :close-on-content-click="false" nudge-bottom="5" left>
          <template v-slot:activator="{ on }">
            <v-icon small v-on="on">mdi-tune-variant</v-icon>
          </template>
          <purchasing-purchase-orders-preferences @update="poRequireApproval=$event.poRequireApproval"/>
        </v-menu>
      </template>
    </breadcrumbs>

    <v-card flat :loading="loading" :disabled="loading">
      <v-form v-model="valid" ref="form" :disabled="readOnly">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>
          <v-row dense v-if="readOnly">
            <v-col class="warning--text mb-4">
              Purchase order is
              <template v-if="po.closed">closed</template>
              <template v-else-if="po.received">partially received</template>
              <template v-else-if="po.confirmed">confirmed</template>
              <template v-else-if="this.po.approved && !this.approvalPermission">approved</template>
              and cannot be modified
            </v-col>
          </v-row>

          <v-row v-if="recurring">
            <v-col><recurring-transaction v-model="recurringTransaction"/></v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="5" md="4">
              <v-row>
                <v-col>
                  <v-autocomplete v-model="po.vendorId" outlined dense label="Supplier"
                                  :items="vendors" item-text="name" item-value="id" @change="changeSupplier" no-data-text="No suppliers found"
                                  :hint="vendor && vendor.suppliesInBond?'Supplies alcohol':(vendor ? '' : 'PO will be saved as NOT APPROVED without a supplier')" persistent-hint
                                  clearable>
                    <template v-slot:prepend-item>
                      <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer" @click="supplierCreate=true">
                        <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add new supplier
                      </div>
                    </template>
                    <template v-slot:item="{item}">
                      <v-list-item-content>{{item.name}}</v-list-item-content>
                      <v-list-item-action class="text-caption">{{item.suppliesInBond?'Supplies alcohol':''}}</v-list-item-action>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field label="Email" v-model="po.email" :rules="[fieldRules.maxLength(100)]" outlined dense :counter="100"
                                hint="Separate emails with a comma" />
                </v-col>
              </v-row>
              <v-row class="mb-2">
                <v-col>
                  <v-textarea label="Mailing address" v-model="po.vendorAddress" outlined dense no-resize :rows="4" :rules="[fieldRules.maxLength(250)]" hide-details="auto" />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" sm="5" md="4" offset-sm="1">
              <v-row dense v-if="!recurring">
                <v-col>
                  <date-picker v-model="po.date" date-only label="Date"  @change="setDueDate(true)"/>
                </v-col>
                <v-col>
                  <v-text-field label="Number" v-model="po.number" outlined dense hint="Overridden if accounting sytem doesn't allow non-system assigned numbers"  :rules="[fieldRules.maxLength(20)]"/>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col v-if="!recurring" cols="6">
                  <date-picker label="Expected" v-model="po.due" date-only/>
                </v-col>
                <v-col cols="6">
                  <v-text-field label="Ship via" v-model="po.shipVia" outlined dense :counter="20" :rules="[fieldRules.maxLength(20)]" />
                </v-col>
              </v-row>
              <v-row dense>
                <v-col v-if="poRequireApproval">
                  <v-checkbox dense label="Approved" v-model="po.approved" :disabled="!approvalPermission || readOnly"
                              persistent-hint :hint="approvalPermission?'':'You don\'t have permission to approve'"/>
                </v-col>
                <v-col>
                  <v-checkbox dense label="Confirmed" v-model="po.confirmed" persistent-hint hint="Supplier acknowledged PO"
                              :disabled="(!recurring && (po.closed || po.received)) || !po.approved"/>
                </v-col>
              </v-row>
            </v-col>
            <v-col class="text-h6 text-right pr-6" v-if="$vuetify.breakpoint.mdAndUp">
              Amount {{vendor && this.company.settings.homeCurrency !== vendor.currency ? vendor.currency : ''}} {{total | currency}}
            </v-col>
          </v-row>

          <v-row dense>
            <v-spacer/>
            <v-col cols="12" sm="5" lg="3">
              <v-switch label="Amounts are inclusive of tax" v-model="po.taxInclusive" dense hide-details class="mt-0" @change="toggleTaxInclusive()"/>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col>
              <outlined-block v-model="po.lines" :rules="[lines]" :disabled="readOnly">
                <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                              :items="po.lines" no-data-text="There must be at least one item" item-key="key"
                              :headers="headers"
                              :item-class="item => item.key === selectedItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              class="no-sort" ref="lineTableBody"
                              @click.native.stop=""
                              @click:row="item => {selectedItem = item.key; $nextTick(() => this.$refs.form.validate())}">
                  <template v-slot:item.add="{index}">
                    <v-icon class="handle mt-n3" :disabled="readOnly">mdi-drag</v-icon>
                    <v-btn class="mt-n3" icon @click.stop="deleteLine(index)" x-small :disabled="readOnly"><v-icon>mdi-delete</v-icon></v-btn>
                  </template>
                  <template v-slot:item.item="{item}">
                    <template v-if="selectedItem === item.key">
                      <multi-level-select :menuData="types" :first.sync="item.type" :second.sync="item.id" caption @change="setLineDetails(item)"
                                    @addSpirit="inputLine=item;spiritCreate=true"
                                    @addWine="inputLine=item;wineCreate=true"
                                    @addNonExcise="inputLine=item;nonExciseCreate=true"
                                    @addItem="inputLine=item;itemCreate=true"
                                    outlined label="Item"/>
                      <v-text-field label="Description" dense v-model="item.description" :rules="[fieldRules.required,fieldRules.maxLength(100)]" placeholder="Description" class="text-caption" outlined/>
                    </template>
                    <div v-else class="pl-1">
                      <div class="text-caption" :class="{'error--text':!item.type || !item.id}">{{itemDescription(item)}}</div>
                      <div :class="{'error--text':!item.description}">{{item.description || 'Description required'}}</div>
                    </div>
                  </template>
                  <template v-slot:item.sku="{item}">
                    <template v-if="!item.items">
                      <v-text-field v-if="selectedItem === item.key" label="SKU" dense outlined v-model="item.sku" class="text-caption" :rules="[fieldRules.maxLength(20)]"/>
                      <div v-else class="pl-1">{{item.sku}}</div>
                    </template>
                  </template>
                  <template v-slot:item.quantity="{item}">
                    <decimal-number-input v-if="selectedItem === item.key" prefix="" :suffix="suffix(item)" :precision="precision(item)" outlined label="Quantity"
                                          class="text-caption" v-model="item.quantity" @change="changeQuantityPrice(item)"/>
                    <div v-else class="pr-1" :class="{'error--text':item.quantity < 0}">{{item.quantity | number(precision(item))}} {{suffix(item)}}</div>
                  </template>
                  <template v-slot:item.receivedQuantity="{item}">
                    <div class="grey--text">{{item.receivedQuantity | number(precision(item))}}</div>
                  </template>
                  <template v-slot:item.rate="{item}">
                    <decimal-number-input v-if="selectedItem === item.key" label="Unit price" v-model="item.unitPrice" :precision="4" class="text-caption" @change="changeQuantityPrice(item)"/>
                    <div v-else class="pr-1" :class="{'error--text':item.unitPrice<0}">{{item.unitPrice | currency}}</div>
                  </template>
                  <template v-slot:item.amount="{item}">
                    <decimal-number-input v-if="selectedItem === item.key" label="Amount" v-model="item.amount" class="text-caption" @change="changeAmount(item)"/>
                    <div v-else class="pr-1" :class="{'error--text':item.amount<0}">{{item.amount | currency}}</div>
                  </template>
                  <template v-slot:item.taxes="{item}">
                    <v-autocomplete v-if="selectedItem === item.key" dense label="Tax" outlined v-model="item.taxCodeId"
                              :items="taxes" item-text="name" item-value="id" class="text-caption" @change="calculateTaxes()"/>
                    <div v-else class="pl-1">{{itemTax(item)}}</div>
                  </template>
                  <template v-slot:footer="{widths}">
                    <v-divider/>
                    <div @click="selectedItem = null" style="width:100%" class="py-2 px-0">
                      <div :style="'width:'+widths[0]+'px'" class="text-center">
                        <v-btn fab color="primary" x-small @click.stop="addLine" :disabled="readOnly"><v-icon small>mdi-plus</v-icon></v-btn>
                      </div>
                    </div>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="6" order="2" order-sm="1">
              <v-container class="pa-0">
                <v-row>
                  <v-col>
                    <v-textarea label="Message to supplier" v-model="po.memo" outlined dense rows="2" :rules="[fieldRules.maxLength(1000)]" hide-details="auto"/>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-textarea label="Private note" v-model="po.privateNote" outlined dense rows="2" :rules="[fieldRules.maxLength(1000)]" hide-details="auto"/>
                  </v-col>
                </v-row>
                <v-row dense>
                  <v-col cols="1">
                    <attachment v-model="po.attachmentURL" @change="po.attachmentUpload=po.attachmentURL" @delete="po.attachmentDelete=true"/>
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
            <v-col cols="12" sm="6" order="1" order-sm="2">
              <v-container class="pa-0">
                <v-row dense>
                  <v-spacer/>
                  <v-col cols="8" class="font-weight-medium text-right">Subtotal</v-col>
                  <v-col cols="4" class="text-right pr-6">{{subTotal | currency}}</v-col>
                </v-row>
                <template v-for="tax of po.taxLines">
                  <v-row dense v-if="tax.name!=='Exempt' && tax.name!=='Out of Scope'" :key="tax.id" align="center">
                    <v-col cols="8" class="font-weight-medium text-right">{{po.taxInclusive?'Includes ':''}}{{tax.name}} @{{tax.percent}}% on {{tax.onAmount | currency}}</v-col>
                    <v-col cols="3" offset="1" class="text-right pr-6">
                      <decimal-number-input v-model="tax.amount" hide-details no-minimum/>
                    </v-col>
                  </v-row>
                </template>
                <v-row dense>
                  <v-spacer/>
                  <v-col cols="8" class="font-weight-medium text-right">Total {{vendor && company.settings.homeCurrency !== vendor.currency ? `(${vendor.currency})` : ''}}</v-col>
                  <v-col cols="4" class="text-right pr-6">{{total | currency}}</v-col>
                </v-row>
              </v-container>
            </v-col>
          </v-row>

        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="pr-6">
        <v-switch dense hide-details label="Make recurring" class="mt-0" v-model="recurring"
                  v-if="company.allowRecurringTransactions && (!poId || !po.recurringTransaction)" :disabled="onlyRecurring"/>
        <v-spacer/>
        <v-btn v-if="readOnly" color="primary" @click="cancel">Close</v-btn>
        <v-btn v-else plain color="primary" @click="cancel">Cancel</v-btn>
        <v-btn v-if="!readOnly" color="primary" :loading="saving" @click="save">Save{{recurring?' template':''}}</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
    <supplier-quick-create v-model="supplierCreate" @create="newSupplier"/>
    <inventory-items-quick-create v-model="itemCreate" @create="itemCreated"/>
    <spirit-quick-create v-model="spiritCreate" @create="spiritCreated"/>
    <wine-quick-create v-model="wineCreate" @create="wineCreated"/>
    <alcohol-non-excise-quick-create v-model="nonExciseCreate" @create="nonExciseCreated"/>
  </div>
</template>

<style>
  .pt-table-top {vertical-align: top;padding-bottom:10px !important}
</style>

<script>
import {format,add,addBusinessDays} from 'date-fns'
import sortableJS from 'sortablejs';
import SupplierQuickCreate from '@/views/purchasing/suppliers/SupplierQuickCreate'
import InventoryItemsQuickCreate from '@/views/inventory/items/InventoryItemsQuickCreate'
import SpiritQuickCreate from '@/views/alcohol/spirits/SpiritQuickCreate'
import WineQuickCreate from '@/views/alcohol/wine/WineQuickCreate'
import AlcoholNonExciseQuickCreate from '@/views/alcohol/nonExcise/AlcoholNonExciseQuickCreate'
import confirmLeave from '@/mixins/confirmLeave'
import PurchasingPurchaseOrdersPreferences from '@/views/purchasing/purchaseOrders/PurchasingPurchaseOrdersPreferences'
import createHierarchyList from '@/mixins/createHierarchyList'
import Attachment from '@/components/Attachment'
import RecurringTransaction from '@/components/RecurringTransaction'

export default {
  name: 'PurchasingPurchaseOrdersEdit',
  mixins: [confirmLeave,createHierarchyList],
  components: {SupplierQuickCreate,InventoryItemsQuickCreate,SpiritQuickCreate,WineQuickCreate,AlcoholNonExciseQuickCreate,PurchasingPurchaseOrdersPreferences,Attachment,RecurringTransaction},
  props: {
    poId: {type:String, required:false},
    vendorId: {type:String, required:false},
    itemType: {type:String, required:false},
    itemId: {type:String, required:false},
    copyPOId: {type:String, required:false},
    onlyRecurring: {type:Boolean, required:false},
    fromRecurringTransactionId: {type:String, required:false},
    date: {type:String, required:false}
  },
  data () {
    return {
      recurring: this.onlyRecurring,
      recurringTransaction: {},
      taxes: [],
      vendors: [],
      supplierCreate:false,
      poRequireApproval: false,
      spirits: [],
      wines: [],
      nonExcises: [],
      items: [],
      itemCategories: [],
      products: [],
      productCategories: [],
      po: {vendorId:'',vendorAddress:'',date:this.date ?? format(new Date(), 'yyyy-MM-dd'),due:this.date ?? format(new Date(), 'yyyy-MM-dd'),shipVia:'',
           taxInclusive:false,lines:[],taxLines:[],memo:'',privateNote:'',number:'',
           approved: true,
           confirmed:false},
      confirmed: false,
      dirty: false,
      saving: false,
      loading: false,
      valid: true,
      headers: [{text:'',value:'add',width:'5%',align:'center',cellClass:'pa-0',divider:true},
                {text: 'Item', value: 'item', width:'25%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true},
                {text: 'SKU', value: 'sku' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true},
                {text: 'Quantity', value: 'quantity' ,width:'15%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                {text: 'Received', value: 'receivedQuantity' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                {text: 'Rate', value: 'rate' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                {text: 'Amount', value: 'amount' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                {text: 'Tax', value: 'taxes' ,width:'10%',cellClass:'py-2 px-1 pt-table-top',divider:true}],
      lines: v => v.length>0 || 'There must be at least one line item',
      itemValid: id => id !== '' || 'Please select',
      selectedItem: null,
      inputLine: null,
      itemCreate: false,
      spiritCreate: false,
      wineCreate: false,
      nonExciseCreate: false,
      sortable: null
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    readOnly() {
      return !this.recurring && (this.po.closed || this.confirmed ||  this.po.received || (this.po.approved && !this.approvalPermission));
    },
    approvalPermission() {
      return this.company.permissions.includes('po.approve');
    },
    vendor() {
      return this.vendors.find(vendor => vendor.id === this.po.vendorId);
    },
    subTotal() {
      return this.po.lines.reduce((total,line) => {return total + line.amount}, 0)
    },
    total() {
      return this.po.taxInclusive ? this.subTotal : this.po.taxLines.reduce((total,tax) => tax.amount + total, 0) + this.subTotal;
    },
    types() {
      const types = [];

      if (this.itemCategories.length) {
        const categories = this.createHierarchyList(this.itemCategories);
        for (const category of categories) {
          const sub = this.items.filter(item => item.category?.id === category.id);
          if (sub.length)
            category.menu = sub;
          else
            category.disabled = true;
        }
        const otherItems = {name:'Other items',id:'x'};
        const leftOver = this.items.filter(item => !item.category?.id);
        if (leftOver.length)
          otherItems.menu = leftOver;
        else
          otherItems.disabled = true;
        types.push({name:'Item',id:'Item',menu:[...categories, {divider:true}, otherItems],actionEvent:'addItem',actionText:'Add new item'});
      }
      else
        types.push({name:'Item',id:'Item',menu:this.items,actionEvent:'addItem',actionText:'Add new item'});

      if (this.vendor?.suppliesInBond) {
        types.push({name:'Spirit',id:'Spirit',menu:this.spirits,actionEvent:'addSpirit',actionText:'Add new spirit'});
        types.push({name:'Wine',id:'Wine',menu:this.wines,actionEvent:'addWine',actionText:'Add new wine'});
      }
      types.push({name:'Non-excise',id:'NonExcise',menu:this.nonExcises,actionEvent:'addNonExcise',actionText:'Add new non-excise'});

      if (this.vendor?.suppliesInBond)
        lineTypeProducts(this, 'in-bond');
      else
        lineTypeProducts(this, 'duty-paid');

      return types;

      function lineTypeProducts(_this, suffix) {
        if (_this.products.length) {
          if (_this.productCategories.length) {
            const categories = _this.createHierarchyList(_this.productCategories);
            for (const category of categories) {
              const sub = _this.products.filter(product => product.category?.id === category.id);
              if (sub.length)
                category.menu = sub;
              else
                category.disabled = true;
            }
            types.push({name:`Product (${suffix})`,id:'Product',menu:categories});
            const leftOver = _this.products.filter(product => !product.category?.id);
            if (leftOver.length)
              types[types.length-1].menu.push({divider:true},{name:'Other products',id:'x',menu:leftOver});
          }
          else
            types.push({name:`Product (${suffix})`,id:'Product',menu:_this.products});
        }
      }

    }
  },
  watch: {
    po: {
      handler() {this.dirty = true;},
      immediate: true,
      deep: true
    }
  },
  async created() {
    this.loading = true;
    await Promise.all([
      this.$lazyListLoad(this.productCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Product'}]),
      this.$lazyListLoad(this.itemCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Item'}]),
      this.$lazyListLoad(this.products, 'products2', 'id,name,category{id}', 100, !this.poId ? [{field:'active', comparator:'eq', value:'true'}] : []),
      this.$lazyListLoad(this.vendors, 'vendors2', 'id,name,suppliesInBond,currency', 100, !this.poId ?  [{field:'active', comparator:'eq', value:'true'}] : []),
      this.$lazyListLoad(this.items, 'items2', 'id,name,buyingUnits,buyingPrecision,category{id}', 100, !this.poId ? [{field:'active', comparator:'eq', value:'true'}] : []),
      this.$lazyListLoad(this.spirits, 'spirits2', 'id,name', 100, this.poId ? [] : [{field:'active', comparator:'eq', value:'true'}]),
      this.company.allowWine ? this.$lazyListLoad(this.wines, 'wines2', 'id,name', 100, this.poId ? [] : [{field:'active', comparator:'eq', value:'true'}]) : [],
      this.$lazyListLoad(this.nonExcises, 'nonExcises2', 'id,name', 100, this.poId ? [] : [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(this.taxes, 'taxes2', 'id,name,purchaseRate', 100, this.poId ? [] : [{field:'active', comparator:'eq', value:'true'},{field:'purchaseRate', comparator:'ne', value:''}])
    ]);
    const results = await this.$apiCall(`query PurchasingPurchaseOrdersEdit($edit:Boolean!, $id:ID!) {
                                              purchaseOrderPreferences {poRequireApproval}
                                              purchaseOrder(id:$id) @include(if:$edit) {
                                                id,vendorId,vendorAddress,email,date,due,closed,confirmed,approved,received,shipVia,memo,privateNote,number,attachmentURL
                                                taxInclusive,
                                                lines {type,id,sku,description,quantity,receivedQuantity,unitPrice,amount,taxCodeId}
                                                taxLines {
                                                  id,name,amount,onAmount,percent
                                                }
                                                recurringTransaction {
                                                  name,scheduleType,advanceDays,startDate,scheduleInterval
                                                  frequency,dayOfWeek,ordinal,day,month
                                                  end,endDate,endOccurances
                                                }
                                              }
                                              nextPurchaseOrderNumber
                                          }`,
                                        {edit:this.poId!=undefined || this.copyPOId != undefined,
                                         id:this.poId ?? this.copyPOId ?? ''});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }

    this.poRequireApproval = results.data.purchaseOrderPreferences.poRequireApproval;

    if (this.poId || this.copyPOId) {
      this.po = results.data.purchaseOrder;
      let key = Date.now();
      this.po.lines.forEach(line => {line.key = key++; line.receivedQuantity = this.copyPOId ? 0 : line.receivedQuantity});

      for (const line of this.po.lines) {
        if (this.po.taxInclusive && line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          if (this.po.taxInclusive)
            line.unitPrice += line.unitPrice * tax.purchaseRate/100;
          line.amount = this.$roundNumber(line.unitPrice * line.quantity);
        }
      }

      this.confirmed = this.po.confirmed;
      this.recurring = !!this.po.recurringTransaction && !this.copyPOId;
      this.recurringTransaction = this.po.recurringTransaction ?? {};
    }
    if (!this.poId) {
      this.po.approved = !this.poRequireApproval || this.company.permissions.includes('po.approve');
      if (this.copyPOId) {
        this.po.confirmed = false;
        this.confirmed = false;
        this.po.closed = false;
        this.po.received = false;
        this.po.date = this.date ?? format(new Date(), 'yyyy-MM-dd');
        this.po.due = this.po.date;
      }
      this.po.number = results.data.nextPurchaseOrderNumber;
      if (this.vendorId) {
        this.po.vendorId = this.vendorId;
        this.changeSupplier();
      }
      if (this.itemType && this.itemId) {
        const line = await this.addLine(null, this.itemType, this.itemId);
        const source = this.getSourceItem(line);
        if (source?.preferredVendor?.id) {
          this.po.vendorId = source.preferredVendor.id;
          this.changeSupplier();
        }
      }
    }
    this.$nextTick(() => {this.dirty = false});
    this.loading = false;
  },
  mounted() {
    const _self = this;
    this.sortable = sortableJS.create(this.$refs.lineTableBody.$el.querySelector('tbody'), {
      handle: '.handle',
      onEnd({newIndex, oldIndex}) {
        const rowSelected = _self.po.lines.splice(oldIndex, 1)[0];
        _self.po.lines.splice(newIndex, 0, rowSelected);
      }
    })

    if (!this.poId)
      this.validate();
//      this.$refs.form.validate();
  },
  beforeDestroy() {
    this.sortable.destroy();
  },

  methods: {
    itemDescription(item) {
      if (!item.type)
        return 'MISSING';
      const type = item.type === 'NonExcise' ? 'Non-excise alcohol' : item.type;
      const list = {'Product':this.products, 'Item': this.items, 'Spirit': this.spirits, 'Wine':this.wines, 'NonExcise':this.nonExcises};
      return `${type}: ${list[item.type].find(i => i.id === item.id)?.name ?? 'MISSING'}`;
    },
    itemTax(item) {
      return this.taxes.find(tax => tax.id === item.taxCodeId)?.name;
    },
    validate() {
      this.$nextTick(() => this.$refs.form.validate());
    },
    suffix(line) {
      return {'Spirit':'LAA','Wine':'litres','NonExcise':'litres','Product':'cases'}[line.type] ?? (this.items.find(i => i.id === line.id)?.buyingUnits ?? '');
    },
    precision(line) {
      return {'Spirit':3,'Wine':3,'NonExcise':3,'Product':1}[line.type] ?? (this.items.find(i => i.id === line.id)?.buyingPrecision ?? 0);
    },
    async itemCreated(item) {
      this.items = (await this.$store.dispatch('items')).filter(item => item.active);
      if (this.inputLine) {
        this.inputLine.type = 'Item';
        this.inputLine.id = item.id;
        this.setLineDetails(this.inputLine);
      }
    },
    spiritCreated(spirit) {
      this.spirits.push(spirit);
      if (this.inputLine) {
        this.inputLine.type = 'Spirit';
        this.inputLine.id = spirit.id;
        this.setLineDetails(this.inputLine);
      }
    },
    wineCreated(wine) {
      this.wines.push(wine);
      if (this.inputLine) {
        this.inputLine.type = 'Wine';
        this.inputLine.id = wine.id;
        this.setLineDetails(this.inputLine);
      }
    },
    nonExciseCreated(nonExcise) {
      this.nonExcises.push(nonExcise);
      if (this.inputLine) {
        this.inputLine.type = 'NonExcise';
        this.inputLine.id = nonExcise.id;
        this.setLineDetails(this.inputLine);
      }
    },
    async addLine(e, type='',id='') {
//      const line = {type,id,quantity:0,unitPrice:0,amount:0,taxCodeId:this.taxes.length?this.taxes[0].id:null,description:'',key:Date.now()};
      const line = {type,id,quantity:0,receivedQuantity:0,unitPrice:0,amount:0,taxCodeId:null,description:'',key:Date.now()};
      await this.setLineDetails(line);
      this.po.lines.push(line);
      this.selectedItem = line.key;
      this.validate();
      return line;
    },
    deleteLine(index) {
      this.po.lines.splice(index, 1);
      this.calculateTaxes();
    },
    getSourceItem(line) {
      const list = {'Item':this.items,'Spirit':this.spirits,'Wine':this.wines,'WineHigh':this.wines,'WineMedium':this.wines,'WineWeak':this.wines,'NonExcise':this.nonExcises,'Product':this.products};
      return list[line.type]?.find(item => item.id === line.id);
    },
    async setLineDetails(line) {
      if (line.id === '')
        return;
      const source = this.getSourceItem(line);
      let type = line.type.toLowerCase();
      if (line.type === 'NonExcise')
        type = 'nonExcise';

      if (!source.loaded) {
        this.loading = true;
        const results = await this.$apiCall(`query PurchasingPurchaseOrdersEdit($id:ID!) {
                                                ${type}(id:$id) {
                                                  sku,buyingDescription,reorderQuantity,reorderDays,reorderBusinessDays,
                                                  preferredVendor{id},vendors{vendorId,tax{id},sku,cost}
                                                }
                                              }`,
                                              {id:line.id});
        this.loading = false;
        if (!results.error) {
          Object.assign(source, results.data[type]);
          source.loaded = true;
        }
      }

      const vendor = source.vendors.find(vendor => vendor.vendorId === this.po.vendorId);

      this.setDueDate(false);
      line.sku = vendor?.sku || source.sku;
      line.quantity = line.quantity || source.reorderQuantity || 1;
      line.unitPrice = vendor?.cost || line.unitPrice;
      this.changeQuantityPrice(line);
      line.taxCodeId = vendor?.tax.id || line.taxCodeId;
      line.description = source.buyingDescription || source.name;
      if (this.po.taxInclusive && line.taxCodeId) {
        const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
        line.unitPrice += line.unitPrice * tax.purchaseRate/100;
        line.amount = this.$roundNumber(line.unitPrice * line.quantity);
      }
      this.calculateTaxes();
    },
    toggleTaxInclusive() {
      for (const line of this.po.lines) {
        if (line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          if (this.po.taxInclusive)
            line.unitPrice += line.unitPrice * tax.purchaseRate/100;
          else
            line.unitPrice = line.unitPrice / (1 + tax.purchaseRate/100)
          line.amount = this.$roundNumber(line.unitPrice * line.quantity);
        }
      }
      this.calculateTaxes();
    },
    setDueDate(force) {
      if (force)
        this.po.due = this.po.date;
      this.po.lines.forEach(line => {
        const item = this.getSourceItem(line);
        if (item) {
          let date = new Date(`${this.po.date}T00:00:00`);
          date = item.reorderBusinessDays ? addBusinessDays(date, item.reorderDays) : add(date, {days:item.reorderDays});
          const due = format(date, 'yyyy-MM-dd');
          this.po.due = due > this.po.due ? due : this.po.due;
        }
      });
    },
    newSupplier(supplier) {
      this.vendors.push(supplier);
      this.po.vendorId = supplier.id;
      this.changeSupplier();
    },
    async changeSupplier() {
      if (!this.vendor) {
        this.po.email = '';
        this.po.vendorAddress = '';
        return;
      }

      if (!this.vendor.suppliesInBond)
        this.po.lines = this.po.lines.filter(line => line.type === 'Item');

      if (!this.vendor.loaded) {
        this.loading = true;
        const results = await this.$apiCall(`query PurchasingPurchaseOrdersEdit($id:ID!) {
                                                vendor(id:$id) {
                                                  id,name,email,currency,
                                                  billingAddress{line1,line2,city,province,postalCode}
                                                }
                                              }`,
                                              {id:this.vendor.id});
        this.loading = false;
        if (!results.error) {
          this.vendor.loaded = true;
          this.$set(this.vendor, 'email', results.data.vendor.email);
          Object.assign(this.vendor, results.data.vendor);
        }
      }
      this.po.email = this.vendor.email;
      const address = this.vendor.billingAddress;
      this.po.vendorAddress = this.vendor.name + '\n' + address.line1 + '\n' + (address.line2 ? address.line2 + '\n' : '') +
                              address.city + ' ' + address.province + '  ' + address.postalCode;
      for (const line of this.po.lines)
        this.setLineDetails(line);
      this.calculateTaxes();
    },
    changeQuantityPrice(line) {
      line.amount = this.$roundNumber(line.quantity * line.unitPrice);
      this.calculateTaxes();
    },
    changeAmount(line) {
      line.unitPrice = line.quantity ? this.$roundNumber(line.amount / line.quantity, 4) : 0;
      this.calculateTaxes();
    },
    cancel() {
      this.$router.back();
    },
    isLineIncomplete(line) {
      return !line.type || !line.id || !line.description ||
              line.quantity < 0 ||
              line.unitPrice < 0 ||
              line.amount < 0;
    },
    calculateTaxes() {
      this.po.taxLines = [];
      this.po.lines.forEach(line => {
        if (line.taxCodeId) {
          let tax = this.po.taxLines.find(tax => tax.id === line.taxCodeId);
          if (!tax) {
            const t = this.taxes.find(t => t.id === line.taxCodeId);
            tax = {id:line.taxCodeId,name:t.name,percent:t.purchaseRate,amount:0,onAmount:0};
            this.po.taxLines.push(tax);
          }
          tax.onAmount += this.po.taxInclusive ? line.amount/(1+tax.percent/100) : line.amount;
          tax.amount += this.$roundNumber(this.po.taxInclusive ? line.amount - line.amount/(1+tax.percent/100) : line.amount * tax.percent / 100);
        }
      });
    },
    async save() {
      if (!this.$refs.form.validate() || this.po.lines.some(line => this.isLineIncomplete(line))) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }

      this.saving = true;
      // eslint-disable-next-line no-unused-vars
      const {lines,closed,received,attachmentURL,recurringTransaction,...input} = this.po;
      input.lines = this.po.lines.map(line => {
        // eslint-disable-next-line no-unused-vars
        const {receivedQuantity,key,...newLine} = line;

        if (input.taxInclusive && newLine.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === newLine.taxCodeId);
          newLine.amount = this.$roundNumber(newLine.amount / (1 + tax.purchaseRate/100));
          newLine.unitPrice = newLine.quantity ? newLine.amount / newLine.quantity : 0;
        }

        return newLine;
      });

      // If copying or existing PO is made recurring, create a new recurring PO (and leave original unchanged)
      if (this.copyPOId || this.recurring && !this.po.recurringTransaction)
        delete input.id;

      if (this.recurring)
        input.recurringTransaction = this.recurringTransaction;
      input.fromRecurringTransactionId = this.fromRecurringTransactionId;
      const results = await this.$apiCall(`mutation PurchasingPurchaseOrdersEdit($input: PurchaseOrderInput!) {
                                             purchaseOrderCreate(input:$input) {id}
                                           }`,
                                          {input})
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${input.id?'Updated':'Created'} ${this.recurring?'recurring':''} purchase order ${this.recurring?'template':''} ${this.vendor ? `to ${this.vendor.name}`: ''}`});
      }
    }
  }
}
</script>